import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, Empty, List, Space, Spin, Tag, Typography } from 'antd';
import { BUYER_ACCOUNT_API, SaleApiError, SaleBuyerSession, SaleOwnedResource, SalePage, saleBuyerPath, saleRequest } from '../../services/saleLinks';
import { detectSaleEmbeddedBrowser, saleDate, saleError, saleStatus, useSaleText } from './saleUi';
import SaleAccountLogin from './SaleAccountLogin';
import SaleBrowserNotice from './SaleBrowserNotice';
import './saleLinks.css';

const empty: SalePage<SaleOwnedResource> = { data: [], total: 0, page: 1, pageSize: 20 };
const SaleAccount: React.FC = () => {
  const text = useSaleText();
  const browser = detectSaleEmbeddedBrowser();
  const [session, setSession] = useState<SaleBuyerSession | null>(null);
  const [resources, setResources] = useState(empty);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [revision, setRevision] = useState(0);

  useEffect(() => {
    document.title = text('我的资源 - 图床小镇', 'My resources - Imgbed');
    if (browser) { setLoading(false); return; }
    let active = true;
    setLoading(true); setError('');
    void saleRequest<SaleBuyerSession>(`${BUYER_ACCOUNT_API}/session`).then((next) => {
      if (!active) return;
      setSession(next);
    }).catch((failure) => { if (active) setError(saleError(failure, text)); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [browser, text, revision]);

  useEffect(() => {
    setResources(empty);
    if (!session?.authenticated || browser) return;
    let active = true;
    setLoading(true); setError('');
    void saleRequest<SalePage<SaleOwnedResource>>(`${BUYER_ACCOUNT_API}/resources?page=${page}&pageSize=20`).then((next) => {
      if (active) setResources(next);
    }).catch((failure) => {
      if (!active) return;
      setError(saleError(failure, text));
      if (failure instanceof SaleApiError && failure.code === 'sale.account.loginRequired') {
        setSession({ ...session, authenticated: false, email: null, expiresAt: null });
      }
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [session, page, browser, text]);

  if (browser) return <SaleBrowserNotice browser={browser} />;
  return <div className="sale-page sale-account-page">
    <Typography.Title level={2}>{text('我的资源', 'My resources')}</Typography.Title>
    <Typography.Paragraph type="secondary">{text('这里仅展示绑定当前邮箱的资源。设备绑定的资源请使用原浏览器打开售卖链接。', 'Only resources bound to this email appear here. Open device-bound resources in their original browser.')}</Typography.Paragraph>
    {error && <Alert showIcon type="error" message={error} action={<Button onClick={() => setRevision((value) => value + 1)}>{text('重试', 'Retry')}</Button>} />}
    <Card>{session ? <SaleAccountLogin session={session} onChange={(next) => { setSession(next); setPage(1); setResources(empty); setError(''); }} /> : loading && <Spin />}</Card>
    {session?.authenticated && <Card className="sale-resource-library">
      <Space className="sale-toolbar"><Typography.Title level={4}>{text('已激活资源', 'Activated resources')}</Typography.Title>
        <Button onClick={() => setRevision((value) => value + 1)}>{text('刷新', 'Refresh')}</Button></Space>
      <List loading={loading} dataSource={resources.data}
        locale={{ emptyText: <Empty description={text('暂无账号绑定的资源', 'No account-bound resources yet')} /> }}
        pagination={{ current: page, total: resources.total, pageSize: 20, showSizeChanger: false, onChange: setPage }}
        renderItem={(item) => <List.Item actions={[<Button key="open" type="primary" href={saleBuyerPath(item.linkCode)} disabled={!item.available}>{text('打开资源', 'Open resource')}</Button>]}>
          <List.Item.Meta title={<Space wrap>{item.title}<Tag color={item.available ? 'blue' : 'default'}>{item.available ? text('可使用', 'Available') : text('暂不可用', 'Unavailable')}</Tag></Space>}
            description={<Space direction="vertical" size={2}>
              <span>{text('激活时间：', 'Activated: ')}{saleDate(item.activatedAt)}</span>
              {!item.available && <span>{saleStatus(item.codeStatus, text)} / {saleStatus(item.linkStatus, text)} · {text('资源受卖家设置和原始文件状态限制，请联系卖家。', 'Availability depends on the seller and source file. Contact the seller.')}</span>}
            </Space>} />
        </List.Item>} />
    </Card>}
  </div>;
};
export default SaleAccount;
